import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { db, doc, getDoc, setDoc } from '../firebaseConfig';
import { usePeriod } from './PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const RunningNetPL = ({ userID, trades }) => {
  const { 
    selectedPeriod, setSelectedPeriod, 
    selectedYear, setSelectedYear, 
    selectedMonth, setSelectedMonth, 
    selectedTag, setSelectedTag, 
    showMissedTrades, setShowMissedTrades 
  } = usePeriod();
  const [journalEntries, setJournalEntries] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const years = Array.from({ length: 2 }, (_, i) => new Date().getFullYear() - i);
  const isInitialLoad = useRef(true);

  const loadUserPreferences = async () => {
    try {
      const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
      const prefDoc = await getDoc(prefRef);

      const validPeriods = [
        'today', 'this-week', 'this-month', 'this-year',
        'pick-month', 'pick-year', 'pick-month-year', 'all-time'
      ];

      if (prefDoc.exists()) {
        const prefs = prefDoc.data();
        console.log('Loading preferences from Firebase:', prefs);

        const period = validPeriods.includes(prefs.defaultPeriod) ? prefs.defaultPeriod : 'all-time';
        const currentDate = new Date();
        const month = (prefs.defaultMonth >= 1 && prefs.defaultMonth <= 12) ? prefs.defaultMonth : currentDate.getMonth() + 1;
        const year = prefs.defaultYear || currentDate.getFullYear();
        const tag = prefs.defaultTag || 'all';

        setSelectedPeriod(period);
        setSelectedMonth(month);
        setSelectedYear(year);
        setSelectedTag(tag);
        setShowMissedTrades(prefs.showMissedTrades || false);

        console.log('Loaded preferences:', { period, month, year, tag, showMissedTrades: prefs.showMissedTrades });
      } else {
        const defaults = {
          defaultPeriod: 'all-time',
          defaultMonth: currentDate.getMonth() + 1,
          defaultYear: currentDate.getFullYear(),
          defaultTag: 'all',
          showMissedTrades: false
        };
        await setDoc(prefRef, defaults);

        setSelectedPeriod(defaults.defaultPeriod);
        setSelectedMonth(defaults.defaultMonth);
        setSelectedYear(defaults.defaultYear);
        setSelectedTag(defaults.defaultTag);
        setShowMissedTrades(defaults.showMissedTrades);

        console.log('Set default preferences:', defaults);
      }
    } catch (error) {
      console.error('Error in loadUserPreferences:', error);
    } finally {
      isInitialLoad.current = false;
    }
  };

  const saveUserPreferences = async () => {
    try {
      const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
      const currentPrefs = {
        defaultPeriod: selectedPeriod,
        defaultMonth: selectedMonth,
        defaultYear: selectedYear,
        defaultTag: selectedTag,
        showMissedTrades: showMissedTrades
      };
      console.log('Saving preferences to Firebase:', currentPrefs);
      await setDoc(prefRef, currentPrefs, { merge: true });
      console.log('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    loadUserPreferences();
  }, [userID]);

  useEffect(() => {
    if (!isInitialLoad.current) {
      saveUserPreferences();
    }
  }, [selectedPeriod, selectedMonth, selectedYear]);

  useEffect(() => {
    const processTrades = () => {
      const filteredEntries = trades.filter(trade => trade.status === (showMissedTrades ? 'Missed' : 'Closed'));
      const tags = new Set();
      filteredEntries.forEach(entry => {
        if (Array.isArray(entry.tags)) {
          entry.tags.forEach(tag => tags.add(tag));
        }
      });

      setUniqueTags(Array.from(tags));
      setJournalEntries(filteredEntries);
      console.log('Processed trades:', filteredEntries.length, 'showing', showMissedTrades ? 'Missed' : 'Closed');
    };

    processTrades();
  }, [userID, trades, showMissedTrades]);

  const handlePeriodChange = async (value) => {
    console.log('Period changed to:', value);
    setSelectedPeriod(value);
  };

  const handleMonthChange = async (value) => {
    console.log('Month changed to:', value);
    setSelectedMonth(value);
  };

  const handleYearChange = async (value) => {
    console.log('Year changed to:', value);
    setSelectedYear(value);
  };

  const handleTagChange = async (value) => {
    console.log('Tag changed to:', value);
    setSelectedTag(value);

    try {
      const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
      const prefDoc = await getDoc(prefRef);
      const currentPrefs = prefDoc.exists() ? prefDoc.data() : {
        defaultPeriod: selectedPeriod,
        defaultMonth: selectedMonth,
        defaultYear: selectedYear,
        showMissedTrades: showMissedTrades
      };

      const updatedPrefs = {
        ...currentPrefs,
        defaultTag: value
      };

      console.log('Saving updated preferences:', updatedPrefs);
      await setDoc(prefRef, updatedPrefs, { merge: true });
      console.log('Tag preference saved successfully');
    } catch (error) {
      console.error('Error saving tag preference:', error);
    }
  };

  const handleToggleMissedTrades = async (event) => {
    const newValue = event.target.checked;
    console.log('Toggle changed to:', newValue);
    setShowMissedTrades(newValue);

    try {
      const prefRef = doc(db, `Users/${userID}/preferences/periodSettings`);
      const prefDoc = await getDoc(prefRef);
      const currentPrefs = prefDoc.exists() ? prefDoc.data() : {
        defaultPeriod: selectedPeriod,
        defaultMonth: selectedMonth,
        defaultYear: selectedYear,
        defaultTag: selectedTag
      };

      const updatedPrefs = {
        ...currentPrefs,
        showMissedTrades: newValue
      };

      console.log('Saving updated preferences:', updatedPrefs);
      await setDoc(prefRef, updatedPrefs, { merge: true });
      console.log('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving missed trades preference:', error);
    }
  };

  const processData = () => {
    const netPLData = {};
    const filteredEntries = journalEntries.filter((entry) => {
      const entryDate = new Date(entry.date);
      let matchesPeriod = false;
      if (selectedPeriod === 'this-week') {
        matchesPeriod = entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'today') {
        matchesPeriod = entryDate.toDateString() === new Date().toDateString();
      } else if (selectedPeriod === 'this-month') {
        matchesPeriod = entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'this-year') {
        matchesPeriod = entryDate.getFullYear() === new Date().getFullYear();
      } else if (selectedPeriod === 'pick-month') {
        matchesPeriod = entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-year') {
        matchesPeriod = entryDate.getFullYear() === selectedYear;
      } else if (selectedPeriod === 'pick-month-year') {
        matchesPeriod = entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
      } else {
        matchesPeriod = true; // all-time
      }

      const matchesTag = selectedTag === 'all' || 
        (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));

      return matchesPeriod && matchesTag;
    });

    filteredEntries.forEach((entry) => {
      if (!isNaN(entry.returns)) {
        const entryDate = new Date(entry.date);
        let key;
        if (selectedPeriod === 'this-week') {
          key = `${entryDate.getFullYear()}-W${entryDate.getWeek()}`;
        } else if (selectedPeriod === 'this-month' || selectedPeriod === 'pick-month' || selectedPeriod === 'pick-month-year') {
          key = `${entryDate.getFullYear()}-${entryDate.getMonth() + 1}`;
        } else if (selectedPeriod === 'this-year' || selectedPeriod === 'pick-year') {
          key = `${entryDate.getFullYear()}`;
        } else {
          key = 'all-time';
        }

        if (!netPLData[key]) {
          netPLData[key] = 0;
        }
        netPLData[key] += parseFloat(entry.returns);
      }
    });

    return Object.keys(netPLData).map((key) => ({
      period: key,
      netPL: netPLData[key],
    }));
  };

  const data = useMemo(() => processData(), [journalEntries, selectedPeriod, selectedYear, selectedMonth, selectedTag]);

  const currencyFormatter = (value) => `$${value.toFixed(2)}`;

  const tooltipContent = `The Period Data Controller lets you select specific time periods, automatically updating Net P&L and syncing all dashboard widgets to reflect the selected data.`;

  return (
    <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
      <MDBox display="flex" flexDirection="column" pt={1} px={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-5}
          >
            <TroubleshootIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Period Data & Net P&L {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip title={tooltipContent} placement="bottom-end">
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit">
              {currencyFormatter(data.reduce((sum, entry) => sum + entry.netPL, 0))}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider sx={{ my: 1 }} />
      </MDBox>
      <MDBox px={2} pb={2} sx={{ flexGrow: 1 }}>
        <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <MDBox display="flex" alignItems="center" sx={{ minWidth: '180px' }}>
            <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>Period:</MDTypography>
            <MDInput
              select
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              size="small"
              sx={{ width: '120px' }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="this-week">This Week</MenuItem>
              <MenuItem value="this-month">This Month</MenuItem>
              <MenuItem value="this-year">This Year</MenuItem>
              <MenuItem value="pick-month">Pick Month</MenuItem>
              <MenuItem value="pick-year">Pick Year</MenuItem>
              <MenuItem value="pick-month-year">Month & Year</MenuItem>
              <MenuItem value="all-time">All Time</MenuItem>
            </MDInput>
          </MDBox>
          {(selectedPeriod === 'pick-month' || selectedPeriod === 'pick-month-year') && (
            <MDBox display="flex" alignItems="center" sx={{ minWidth: '120px' }}>
              <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>Month:</MDTypography>
              <MDInput
                select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                size="small"
                sx={{ width: '80px' }}
              >
                {months.map((month, index) => (
                  <MenuItem key={index} value={index + 1}>{month}</MenuItem>
                ))}
              </MDInput>
            </MDBox>
          )}
          {(selectedPeriod === 'pick-year' || selectedPeriod === 'pick-month-year') && (
            <MDBox display="flex" alignItems="center" sx={{ minWidth: '120px' }}>
              <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>Year:</MDTypography>
              <MDInput
                select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                size="small"
                sx={{ width: '80px' }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </MDInput>
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" sx={{ minWidth: '180px' }}>
            <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>Tag:</MDTypography>
            <MDInput
              select
              value={selectedTag}
              onChange={(e) => handleTagChange(e.target.value)}
              size="small"
              sx={{ width: '120px' }}
            >
              <MenuItem value="all">All Tags</MenuItem>
              {uniqueTags.map((tag) => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </MDInput>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={showMissedTrades}
                  onChange={handleToggleMissedTrades}
                  color="primary"
                  size="small"
                />
              }
              label={<MDTypography variant="caption" color="text">Missed Trades</MDTypography>}
              sx={{ m: 0 }}
            />
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
};

RunningNetPL.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
};

export default RunningNetPL;